import { useState, useRef } from 'react';
import { api } from '../lib/api.js';

interface QueryResult {
  columns: string[];
  rows: Record<string, unknown>[];
  meta: {
    duration: number;
    changes: number;
    rows_read: number;
    rows_written: number;
    row_count: number;
  };
}

interface HistoryEntry {
  sql: string;
  timestamp: number;
  success: boolean;
}

const TABLE_NAMES = [
  'amazon_orders', 'amazon_returns_daily',
  'amazon_products', 'amazon_catalog', 'amazon_inventory_current', 'amazon_inventory_history',
  'amazon_inventory_planning', 'amazon_storage_fees_monthly',
  'amazon_inbound_shipments', 'amazon_inventory_by_fc', 'amazon_fulfillment_centers',
  'amazon_ads_profiles', 'amazon_ppc_campaigns', 'amazon_ppc_ad_groups', 'amazon_ppc_keywords', 'amazon_ppc_keyword_performance',
  'amazon_ppc_campaign_performance', 'amazon_ppc_search_terms', 'amazon_ppc_actions',
  'amazon_financial_daily', 'product_costs', 'amazon_settlement_entries', 'amazon_settlement_summaries',
  'amazon_competitive_prices', 'amazon_traffic_daily', 'amazon_traffic_store_daily',
  'keyword_rankings', 'keyword_research', 'amazon_search_query_performance', 'amazon_top_search_terms',
  'amazon_message_threads', 'amazon_buyer_messages', 'amazon_review_requests',
  'accounts', 'exports', 'sync_history', 'ai_analyses',
];

const HISTORY_KEY = 'sellvana_admin_query_history';

function loadHistory(): HistoryEntry[] {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) ?? '[]');
  } catch {
    return [];
  }
}

function saveHistory(entries: HistoryEntry[]) {
  localStorage.setItem(HISTORY_KEY, JSON.stringify(entries.slice(0, 20)));
}

export function AdminQuery() {
  const [sql, setSql] = useState('');
  const [result, setResult] = useState<QueryResult | null>(null);
  const [error, setError] = useState('');
  const [running, setRunning] = useState(false);
  const [allowWrites, setAllowWrites] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>(loadHistory);
  const [showHistory, setShowHistory] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const runQuery = async () => {
    if (!sql.trim()) return;

    const isWrite = /^\s*(INSERT|UPDATE|DELETE|REPLACE)\b/i.test(sql);
    if (isWrite && !allowWrites) {
      setError('Enable "Allow writes" to run INSERT/UPDATE/DELETE statements.');
      return;
    }
    if (isWrite && !confirm('This will modify data. Continue?')) return;

    setRunning(true);
    setError('');
    setResult(null);

    try {
      const data = await api.post<QueryResult>('/admin/query', { sql, allowWrites });
      setResult(data);
      const entry: HistoryEntry = { sql, timestamp: Date.now(), success: true };
      const updated = [entry, ...history.filter((h) => h.sql !== sql)].slice(0, 20);
      setHistory(updated);
      saveHistory(updated);
    } catch (err) {
      const msg = (err as Error).message;
      setError(msg);
      const entry: HistoryEntry = { sql, timestamp: Date.now(), success: false };
      const updated = [entry, ...history.filter((h) => h.sql !== sql)].slice(0, 20);
      setHistory(updated);
      saveHistory(updated);
    } finally {
      setRunning(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      runQuery();
    }
  };

  const insertTable = (name: string) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const newSql = sql.slice(0, start) + name + sql.slice(end);
    setSql(newSql);
    setTimeout(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = start + name.length;
    }, 0);
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">SQL Query Editor</h2>

      {/* Quick-insert table buttons */}
      <div className="flex flex-wrap gap-1">
        {TABLE_NAMES.map((t) => (
          <button
            key={t}
            onClick={() => insertTable(t)}
            className="rounded bg-gray-100 px-2 py-0.5 text-xs font-mono text-gray-600 hover:bg-blue-100 hover:text-blue-700 transition-colors"
          >
            {t}
          </button>
        ))}
      </div>

      {/* Editor */}
      <div className="space-y-2">
        <textarea
          ref={textareaRef}
          value={sql}
          onChange={(e) => setSql(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="SELECT * FROM amazon_orders LIMIT 10"
          rows={6}
          className="w-full rounded-lg border border-gray-300 bg-gray-50 p-3 font-mono text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          spellCheck={false}
        />

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={runQuery}
            disabled={running || !sql.trim()}
            className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {running ? 'Running...' : 'Run'} <span className="ml-1 text-xs opacity-70">Ctrl+Enter</span>
          </button>

          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={allowWrites}
              onChange={(e) => setAllowWrites(e.target.checked)}
              className="rounded"
            />
            Allow writes
          </label>

          <button
            onClick={() => setShowHistory(!showHistory)}
            className="ml-auto text-sm text-gray-500 hover:text-gray-700"
          >
            {showHistory ? 'Hide' : 'Show'} History ({history.length})
          </button>
        </div>
      </div>

      {/* History */}
      {showHistory && history.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-1 max-h-48 overflow-y-auto">
          {history.map((h, i) => (
            <div
              key={i}
              onClick={() => { setSql(h.sql); setShowHistory(false); }}
              className="flex items-center justify-between rounded px-2 py-1 cursor-pointer hover:bg-gray-50"
            >
              <code className="text-xs text-gray-700 truncate max-w-lg">{h.sql}</code>
              <div className="flex items-center gap-2">
                <span className={`h-1.5 w-1.5 rounded-full ${h.success ? 'bg-green-400' : 'bg-red-400'}`} />
                <span className="text-xs text-gray-400">{formatTime(h.timestamp)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="space-y-2">
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span>{result.meta.row_count} rows returned</span>
            <span>{result.meta.duration}ms</span>
            {result.meta.changes > 0 && <span>{result.meta.changes} changes</span>}
            <span>{result.meta.rows_read} rows read</span>
          </div>

          {result.columns.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    {result.columns.map((col) => (
                      <th key={col} className="px-3 py-2 font-medium text-xs text-gray-600 whitespace-nowrap">
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((row, i) => (
                    <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                      {result.columns.map((col) => {
                        const val = row[col];
                        const display = val === null ? 'NULL' : String(val);
                        return (
                          <td key={col} className="px-3 py-1.5 font-mono text-xs max-w-xs truncate">
                            <span className={val === null ? 'text-gray-300 italic' : ''}>
                              {display.length > 100 ? display.slice(0, 100) + '…' : display}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
